import {
  Button as HeroButton,
  Card as HeroCard,
  Checkbox as HeroCheckbox,
  Chip,
  Description as HeroDescription,
  Drawer as HeroDrawer,
  Input as HeroInput,
  Label as HeroLabel,
  ListBox as HeroListBox,
  Modal as HeroModal,
  Radio as HeroRadio,
  RadioGroup as HeroRadioGroup,
  Select as HeroSelect,
  Spinner,
  Switch as HeroSwitch,
  Table as HeroTable,
  Tabs as HeroTabs,
  TextArea as HeroTextArea,
} from '@heroui/react';
import type { ButtonProps as HeroButtonProps, CardProps, ChipProps, InputProps, TextAreaProps } from '@heroui/react';
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
type ButtonProps = Omit<HeroButtonProps, 'children' | 'className' | 'isDisabled' | 'isPending' | 'variant'> & {
  variant?: ButtonVariant;
  busy?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  title?: string;
};
type UiInputProps = Omit<InputProps, 'className'> & { className?: string };
type UiTextAreaProps = Omit<TextAreaProps, 'className'> & { className?: string };
type UiCardProps = HTMLAttributes<HTMLDivElement> & Pick<CardProps, 'variant'>;
type BadgeTone = 'slate' | 'amber' | 'blue' | 'green' | 'red' | 'purple';
type DashboardCheckboxProps = Omit<ComponentProps<typeof HeroCheckbox>, 'children' | 'className'> & {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
};
type DashboardSwitchProps = Omit<ComponentProps<typeof HeroSwitch>, 'children' | 'className'> & {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
};
type RadioOption<TValue extends string = string> = {
  value: TValue;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};
type RadioOptionsProps<TValue extends string = string> = Omit<ComponentProps<typeof HeroRadioGroup>, 'children' | 'className' | 'onChange' | 'orientation' | 'value'> & {
  label?: ReactNode;
  description?: ReactNode;
  value: TValue;
  onChange(value: TValue): void;
  options: ReadonlyArray<RadioOption<TValue>>;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
};
type HeroSelectOption = {
  id: string | number;
  label: ReactNode;
  textValue?: string;
  isDisabled?: boolean;
};
type HeroSelectFieldProps = Omit<ComponentProps<typeof HeroSelect>, 'children' | 'className'> & {
  label?: ReactNode;
  description?: ReactNode;
  options: HeroSelectOption[];
  className?: string;
};

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export function Button({
  variant = 'primary',
  busy = false,
  className,
  children,
  disabled,
  title,
  ...props
}: ButtonProps) {
  const heroVariant = variant === 'success' ? 'primary' : variant;
  return (
    <HeroButton
      {...props}
      isDisabled={disabled || busy}
      isPending={busy}
      variant={heroVariant}
      aria-label={props['aria-label'] ?? title}
      className={cx(
        'min-h-10 font-semibold',
        variant === 'success' && 'bg-success text-success-foreground hover:bg-success-hover',
        className,
      )}
    >
      {busy && <Spinner color="current" size="sm" />}
      {children}
    </HeroButton>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="grid gap-1.5 text-sm font-semibold text-[var(--app-brand-ink)]">
      <span className="px-0.5">{label}</span>
      {children}
      {error ? <span className="px-0.5 text-xs text-danger">{error}</span> : hint ? <span className="px-0.5 text-xs font-normal text-[var(--app-brand-muted)]">{hint}</span> : null}
    </label>
  );
}

export function Input({ className, ...props }: UiInputProps) {
  return <HeroInput {...props} fullWidth variant="secondary" className={cx('h-11 border border-[var(--app-brand-line)] bg-white/80 px-3 text-sm shadow-none', className)} />;
}

export function HeroSelectField({ label, description, options, className, ...props }: HeroSelectFieldProps) {
  return (
    <HeroSelect {...props} fullWidth variant="secondary" className={cx('w-full', className)}>
      {label ? <HeroLabel>{label}</HeroLabel> : null}
      <HeroSelect.Trigger>
        <HeroSelect.Value />
        <HeroSelect.Indicator />
      </HeroSelect.Trigger>
      {description ? <HeroDescription>{description}</HeroDescription> : null}
      <HeroSelect.Popover>
        <HeroListBox>
          {options.map((option) => (
            <HeroListBox.Item key={option.id} id={option.id} textValue={option.textValue ?? String(option.id)} isDisabled={option.isDisabled}>
              {option.label}
              <HeroListBox.ItemIndicator />
            </HeroListBox.Item>
          ))}
        </HeroListBox>
      </HeroSelect.Popover>
    </HeroSelect>
  );
}

export function Checkbox({ label, description, className, ...props }: DashboardCheckboxProps) {
  return (
    <HeroCheckbox {...props} variant={props.variant ?? 'secondary'} className={className}>
      <HeroCheckbox.Content>
        <HeroCheckbox.Control>
          <HeroCheckbox.Indicator />
        </HeroCheckbox.Control>
        {label}
      </HeroCheckbox.Content>
      {description ? <HeroDescription>{description}</HeroDescription> : null}
    </HeroCheckbox>
  );
}

export function Switch({ label, description, className, ...props }: DashboardSwitchProps) {
  return (
    <HeroSwitch {...props} className={className}>
      <HeroSwitch.Content>
        <HeroSwitch.Control>
          <HeroSwitch.Thumb />
        </HeroSwitch.Control>
        {label}
      </HeroSwitch.Content>
      {description ? <HeroDescription>{description}</HeroDescription> : null}
    </HeroSwitch>
  );
}

export function RadioOptions<TValue extends string = string>({
  label,
  description,
  value,
  onChange,
  options,
  orientation = 'vertical',
  className,
  ...props
}: RadioOptionsProps<TValue>) {
  return (
    <HeroRadioGroup
      {...props}
      value={value}
      onChange={(nextValue) => onChange(nextValue as TValue)}
      orientation={orientation}
      variant={props.variant ?? 'secondary'}
      className={cx('w-full gap-3', className)}
    >
      {label ? <HeroLabel className="font-semibold text-foreground">{label}</HeroLabel> : null}
      {description ? <HeroDescription>{description}</HeroDescription> : null}
      {options.map((option) => (
        <HeroRadio key={option.value} value={option.value} isDisabled={option.disabled}>
          <HeroRadio.Content className="group flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-sm font-bold transition-colors data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-focus/20 data-[hovered=true]:border-field-border-hover data-[selected=true]:border-accent data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent-soft-foreground">
            <HeroRadio.Control className="mt-0.5 shrink-0">
              <HeroRadio.Indicator />
            </HeroRadio.Control>
            <span className="grid min-w-0 gap-1">
              <span>{option.label}</span>
              {option.description ? <HeroDescription className="font-normal group-data-[selected=true]:text-accent-soft-foreground/80">{option.description}</HeroDescription> : null}
            </span>
          </HeroRadio.Content>
        </HeroRadio>
      ))}
    </HeroRadioGroup>
  );
}

export const Table = HeroTable;
export const DashboardTable = HeroTable;
export const DashboardTabs = HeroTabs;
export const Drawer = HeroDrawer;

export function Textarea({ className, ...props }: UiTextAreaProps) {
  return <HeroTextArea {...props} fullWidth variant="secondary" className={cx('min-h-24 border border-[var(--app-brand-line)] bg-white/80 px-3 py-2 text-sm shadow-none', className)} />;
}

export function Card({ children, className, ...props }: UiCardProps) {
  return <HeroCard {...props} className={cx('app-brand-card rounded-[1.35rem] p-5', className)}>{children}</HeroCard>;
}

export function Badge({ children, tone = 'slate' }: { children: ReactNode; tone?: BadgeTone }) {
  const colors = {
    slate: 'default',
    amber: 'warning',
    blue: 'accent',
    green: 'success',
    red: 'danger',
    purple: 'accent',
  } satisfies Record<BadgeTone, ChipProps['color']>;
  return <Chip color={colors[tone]} size="sm" variant="soft" className="font-bold">{children}</Chip>;
}

export function Modal({ open, onClose, title, children, width = 'max-w-2xl' }: { open: boolean; onClose(): void; title: string; children: ReactNode; width?: string }) {
  return (
    <HeroModal.Backdrop isOpen={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }} variant="blur">
      <HeroModal.Container placement="center" scroll="inside">
        <HeroModal.Dialog className={cx('max-h-[94vh]', width)}>
          <HeroModal.CloseTrigger aria-label="Cerrar" />
          <HeroModal.Header className="border-b border-separator px-6 py-5">
            <HeroModal.Heading className="text-lg font-bold">{title}</HeroModal.Heading>
          </HeroModal.Header>
          <HeroModal.Body className="p-5 sm:p-6">{children}</HeroModal.Body>
        </HeroModal.Dialog>
      </HeroModal.Container>
    </HeroModal.Backdrop>
  );
}

export const DashboardModal = {
  Backdrop: HeroModal.Backdrop,
  Container: HeroModal.Container,
  Dialog: ({ className, ...props }: ComponentProps<typeof HeroModal.Dialog>) => (
    <HeroModal.Dialog {...props} className={cx('max-h-[94vh] rounded-[1.35rem]', className)} />
  ),
  CloseTrigger: ({ className, 'aria-label': ariaLabel, ...props }: ComponentProps<typeof HeroModal.CloseTrigger>) => (
    <HeroModal.CloseTrigger {...props} aria-label={ariaLabel ?? 'Cerrar'} className={cx('text-[var(--app-brand-muted)]', className)} />
  ),
  Header: ({ className, ...props }: ComponentProps<typeof HeroModal.Header>) => (
    <HeroModal.Header {...props} className={cx('border-b border-separator px-6 py-5', className)} />
  ),
  Body: ({ className, ...props }: ComponentProps<typeof HeroModal.Body>) => (
    <HeroModal.Body {...props} className={cx('p-5 sm:p-6', className)} />
  ),
  Footer: ({ className, ...props }: ComponentProps<typeof HeroModal.Footer>) => (
    <HeroModal.Footer {...props} className={cx('border-t border-separator px-6 py-4', className)} />
  ),
  Heading: ({ className, ...props }: ComponentProps<typeof HeroModal.Heading>) => (
    <HeroModal.Heading {...props} className={cx('text-lg font-bold text-[var(--app-brand-ink)]', className)} />
  ),
} as const;

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="grid min-h-52 place-items-center rounded-[1.5rem] border border-dashed border-[var(--app-brand-line)] bg-[var(--app-brand-paper)]/80 p-8 text-center">
      <div>
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl border border-[var(--app-brand-line)] bg-white text-2xl text-[var(--app-brand-red)] shadow-sm">◌</div>
        <h3 className="font-bold text-[var(--app-brand-ink)]">{title}</h3>
        <p className="mt-1 max-w-md text-sm text-[var(--app-brand-muted)]">{description}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

export function LoadingScreen({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="app-brand-shell grid min-h-[50vh] place-items-center rounded-[1.75rem] p-6">
      <div className="app-brand-card grid min-w-[240px] justify-items-center gap-4 rounded-[1.5rem] px-8 py-7 text-center">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[var(--app-brand-ink)] font-black text-white shadow-[0_18px_42px_rgb(26_27_24/0.18)]">EP</div>
        <Spinner color="accent" size="md" />
        <div className="text-sm font-semibold text-[var(--app-brand-muted)]">{label}</div>
      </div>
    </div>
  );
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="app-brand-kicker mb-2">Estoy Pidiendo</p>
        <h1 className="text-2xl font-black tracking-tight text-[var(--app-brand-ink)] sm:text-3xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-[var(--app-brand-muted)]">{description}</p>}
      </div>
      {action}
    </header>
  );
}
